import React from "react";

/**
 * ⚠️ Clinical safety note
 * This calculator provides approximate Oral Morphine Equivalent Daily Dose (oMEDD / MME).
 * Conversions vary by patient factors, opioid tolerance, indication, formulation, and guideline source.
 *
 * Buprenorphine is formulation-dependent and may not behave linearly.
 * This tool:
 *  - Buprenorphine patch: shows a RANGE (low–high) based on published equivalences (interpolated).
 *  - Buprenorphine SL: uses a simple 1:40 (mg bupe : mg oral morphine) approximation.
 * Default behaviour excludes buprenorphine from totals; you can toggle inclusion.
 */

// -----------------------------
// Core opioid rows (linear factor)
// -----------------------------
const OPIOID_ROWS = [
  { key: "morphine_oral", drug: "Morphine", mode: "Oral", unit: "mg/day", factor: 1 },
  { key: "morphine_iv", drug: "Morphine", mode: "IV/SC", unit: "mg/day", factor: 3 },

  { key: "oxycodone_oral", drug: "Oxycodone", mode: "Oral", unit: "mg/day", factor: 1.5 },

  { key: "hydromorphone_oral", drug: "Hydromorphone", mode: "Oral", unit: "mg/day", factor: 4 },
  { key: "hydromorphone_iv", drug: "Hydromorphone", mode: "IV/SC", unit: "mg/day", factor: 20 },

  { key: "codeine_oral", drug: "Codeine", mode: "Oral", unit: "mg/day", factor: 0.15 },
  { key: "tramadol_oral", drug: "Tramadol", mode: "Oral", unit: "mg/day", factor: 0.1 },
  { key: "tapentadol_oral", drug: "Tapentadol", mode: "Oral", unit: "mg/day", factor: 0.4 },

  // Common approximation: 25 mcg/hr ≈ 60 MME/day ⇒ ~2.4 MME per mcg/hr
  { key: "fentanyl_td", drug: "Fentanyl", mode: "Transdermal patch", unit: "mcg/hr", factor: 2.4 },
];

// -----------------------------
// Buprenorphine conversions
// -----------------------------

// Patch equivalence anchor points (mcg/hr -> oral morphine mg/day range).
// Based on published equivalence tables, typical strengths 5/10/20 mcg/hr.
// We'll interpolate between points to support in-between inputs.
const BUPE_PATCH_POINTS = [
  { x: 5, low: 10, high: 15 },
  { x: 10, low: 15, high: 25 },
  { x: 20, low: 35, high: 50 },
];

function asNumber(v) {
  const n = parseFloat(String(v ?? "").replace(/,/g, ""));
  return Number.isFinite(n) ? n : 0;
}

function mm2(value, digits = 2) {
  const n = asNumber(value);
  return Number.isFinite(n) ? n.toFixed(digits) : "0.00";
}

function clamp(n, a, b) {
  return Math.max(a, Math.min(b, n));
}

function lerp(a, b, t) {
  return a + (b - a) * t;
}

// Returns { low, high, note } for buprenorphine patch conversion.
function bupePatchRange(mcgHr) {
  const x = asNumber(mcgHr);

  if (x <= 0) return { low: 0, high: 0, note: "" };

  // Find segment for interpolation
  const pts = BUPE_PATCH_POINTS;

  // Below lowest point: extrapolate using first segment slope
  if (x <= pts[0].x) {
    const p0 = pts[0];
    const p1 = pts[1];
    const t = (x - p0.x) / (p1.x - p0.x);
    return {
      low: Math.max(0, lerp(p0.low, p1.low, t)),
      high: Math.max(0, lerp(p0.high, p1.high, t)),
      note: x < p0.x ? "Extrapolated below 5 mcg/hr" : "",
    };
  }

  // Above highest point: extrapolate using last segment slope
  if (x >= pts[pts.length - 1].x) {
    const pN1 = pts[pts.length - 2];
    const pN = pts[pts.length - 1];
    const t = (x - pN.x) / (pN.x - pN1.x);
    return {
      low: Math.max(0, lerp(pN.low, pN.low + (pN.low - pN1.low), t)),
      high: Math.max(0, lerp(pN.high, pN.high + (pN.high - pN1.high), t)),
      note: x > pN.x ? "Extrapolated above 20 mcg/hr" : "",
    };
  }

  // Between points
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i];
    const p1 = pts[i + 1];
    if (x >= p0.x && x <= p1.x) {
      const t = (x - p0.x) / (p1.x - p0.x);
      return {
        low: lerp(p0.low, p1.low, t),
        high: lerp(p0.high, p1.high, t),
        note: "",
      };
    }
  }

  return { low: 0, high: 0, note: "" };
}

// SL buprenorphine approximation: 1 mg bupe SL ≈ 40 mg oral morphine
function bupeSLToMME(mgPerDay) {
  const mg = asNumber(mgPerDay);
  return mg * 40;
}

// -----------------------------
// Table component
// -----------------------------
class OMEDDTable extends React.Component {
  handleChange = (key, e) => {
    this.props.onChange(key, e.target.value);
  };

  totalMME = () => {
    const { values, includeBupeInTotal } = this.props;

    // Base opioids (linear)
    const base = OPIOID_ROWS.reduce(
      (sum, row) => sum + asNumber(values[row.key]) * row.factor,
      0
    );

    if (!includeBupeInTotal) return base;

    // Bupe SL included (single estimate)
    const bupeSL = bupeSLToMME(values.bupe_sl_mg);

    // Bupe patch included (use midpoint of range)
    const r = bupePatchRange(values.bupe_patch_mcg_hr);
    const bupePatchMid = (r.low + r.high) / 2;

    return base + bupeSL + bupePatchMid;
  };

  renderBupePatchRow(values) {
    const dose = asNumber(values.bupe_patch_mcg_hr);
    const r = bupePatchRange(dose);
    const display =
      dose > 0 ? `${mm2(r.low, 0)}–${mm2(r.high, 0)}` : "0–0";

    return (
      <tr className="border-b last:border-0">
        <td className="py-2 pr-3 font-medium">
          Buprenorphine
          <span className="ml-2 text-[10px] uppercase tracking-wide text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
            range
          </span>
        </td>
        <td className="py-2 pr-3 text-gray-700">Transdermal patch</td>
        <td className="py-2 pr-3">
          <input
            type="number"
            step="any"
            inputMode="decimal"
            className="w-28 md:w-32 rounded-xl border px-3 py-1 focus:outline-none focus:ring-2 focus:ring-sky-500"
            placeholder="0"
            value={values.bupe_patch_mcg_hr ?? ""}
            onChange={(e) => this.handleChange("bupe_patch_mcg_hr", e)}
            aria-label="Buprenorphine patch dose mcg/hr"
          />
        </td>
        <td className="py-2 pr-3 text-gray-600">mcg/hr</td>
        <td className="py-2 pr-0 text-right tabular-nums">
          {dose > 0 ? (
            <div className="flex flex-col items-end">
              <div className="tabular-nums">{display}</div>
              {r.note ? (
                <div className="text-[10px] text-amber-700">{r.note}</div>
              ) : null}
            </div>
          ) : (
            <span className="text-gray-400">—</span>
          )}
        </td>
      </tr>
    );
  }

  renderBupeSLRow(values) {
    const doseMg = asNumber(values.bupe_sl_mg);
    const mme = bupeSLToMME(doseMg);

    return (
      <tr className="border-b last:border-0">
        <td className="py-2 pr-3 font-medium">
          Buprenorphine
          <span className="ml-2 text-[10px] uppercase tracking-wide text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
            SL
          </span>
        </td>
        <td className="py-2 pr-3 text-gray-700">Sublingual</td>
        <td className="py-2 pr-3">
          <input
            type="number"
            step="any"
            inputMode="decimal"
            className="w-28 md:w-32 rounded-xl border px-3 py-1 focus:outline-none focus:ring-2 focus:ring-sky-500"
            placeholder="0"
            value={values.bupe_sl_mg ?? ""}
            onChange={(e) => this.handleChange("bupe_sl_mg", e)}
            aria-label="Buprenorphine sublingual dose mg/day"
          />
        </td>
        <td className="py-2 pr-3 text-gray-600">mg/day</td>
        <td className="py-2 pr-0 text-right tabular-nums">
          {doseMg > 0 ? mm2(mme) : <span className="text-gray-400">—</span>}
        </td>
      </tr>
    );
  }

  render() {
    const { values, title, extraBelow, includeBupeInTotal } = this.props;
    const total = this.totalMME();

    return (
      <div className="bg-white rounded-2xl shadow p-4 md:p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold">{title}</h2>
          <div className="text-xs text-gray-500">Units per route shown below</div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left border-b">
                <th className="py-2 pr-3">Opioid (generic)</th>
                <th className="py-2 pr-3">Mode</th>
                <th className="py-2 pr-3">Dose</th>
                <th className="py-2 pr-3">Unit</th>
                <th className="py-2 pr-3 text-right">oMEDD</th>
              </tr>
            </thead>

            <tbody>
              {OPIOID_ROWS.map((row) => {
                const dose = asNumber(values[row.key]);
                const mme = dose * row.factor;

                return (
                  <tr key={row.key} className="border-b last:border-0">
                    <td className="py-2 pr-3 font-medium">{row.drug}</td>
                    <td className="py-2 pr-3 text-gray-700">{row.mode}</td>
                    <td className="py-2 pr-3">
                      <input
                        type="number"
                        step="any"
                        inputMode="decimal"
                        className="w-28 md:w-32 rounded-xl border px-3 py-1 focus:outline-none focus:ring-2 focus:ring-sky-500"
                        placeholder="0"
                        value={values[row.key] ?? ""}
                        onChange={(e) => this.handleChange(row.key, e)}
                        aria-label={`${row.drug} ${row.mode} dose`}
                      />
                    </td>
                    <td className="py-2 pr-3 text-gray-600">{row.unit}</td>
                    <td className="py-2 pr-0 text-right tabular-nums">{mm2(mme)}</td>
                  </tr>
                );
              })}

              {/* ✅ Buprenorphine rows */}
              {this.renderBupePatchRow(values)}
              {this.renderBupeSLRow(values)}
            </tbody>

            <tfoot>
              <tr>
                <td colSpan={4} className="pt-3 pr-3 text-right font-semibold">
                  Total oMEDD {includeBupeInTotal ? "(buprenorphine included*)" : "(buprenorphine excluded)"}
                </td>
                <td className="pt-3 pr-0 text-right font-bold text-sky-700 tabular-nums">
                  {mm2(total)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>

        {extraBelow}
      </div>
    );
  }
}

// -----------------------------
// Main page component
// -----------------------------
export default class Opioid extends React.Component {
  state = {
    left: {},
    right: {},
    leftReducePct: "",
    includeBupeInTotal: false, // ✅ default safer behaviour
  };

  handleSideChange = (side, key, value) => {
    this.setState((prev) => ({
      [side]: { ...prev[side], [key]: value },
    }));
  };

  clearSide = (side) => {
    this.setState({ [side]: {} });
  };

  sumSide = (side) => {
    const values = this.state[side] || {};
    const includeBupeInTotal = this.state.includeBupeInTotal;

    const base = OPIOID_ROWS.reduce(
      (sum, row) => sum + asNumber(values[row.key]) * row.factor,
      0
    );

    if (!includeBupeInTotal) return base;

    const bupeSL = bupeSLToMME(values.bupe_sl_mg);
    const r = bupePatchRange(values.bupe_patch_mcg_hr);
    const bupePatchMid = (r.low + r.high) / 2;

    return base + bupeSL + bupePatchMid;
  };

  render() {
    const leftTotal = this.sumSide("left");
    const rightTotal = this.sumSide("right");
    const leftReduced = leftTotal * (1 - asNumber(this.state.leftReducePct) / 100);

    return (
      <div className="min-h-screen bg-sky-800 py-6 md:py-10">
        <div className="mx-auto max-w-6xl px-4">
          <header className="mb-6 md:mb-8">
            <h1 className="text-5xl md:text-xl font-semibold text-sky-100 text-center my-10">
              Opioid Conversion (oMEDD)
            </h1>

            <p className="text-sm md:text-base text-slate-100 mt-1 text-center">
              Enter each opioid&apos;s daily dose per route. The calculator totals oMEDD per column.
            </p>

            <p className="text-xs text-slate-100 mt-2 text-center">
              Clinical caution: factors are approximations; adjust for cross-tolerance and follow your local guidelines.
            </p>

            <div className="mt-4 flex items-center justify-center gap-3">
              <label className="flex items-center gap-2 text-xs md:text-sm text-amber-100">
                <input
                  type="checkbox"
                  checked={this.state.includeBupeInTotal}
                  onChange={(e) => this.setState({ includeBupeInTotal: e.target.checked })}
                />
                Include buprenorphine in totals (uses patch midpoint*)
              </label>
            </div>

            <p className="text-[11px] text-amber-100 mt-2 text-center">
              * Patch oMEDD is shown as a range; if included in totals, midpoint of the range is used.
            </p>
          </header>

          <div className="grid grid-cols-2 md:grid-cols-2 gap-4 md:gap-6">
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-200">Left regimen</span>
                <button
                  onClick={() => this.clearSide("left")}
                  className="text-xs text-white rounded-lg border px-2 py-1 hover:bg-slate-100 hover:text-slate-900"
                >
                  Clear
                </button>
              </div>

              <OMEDDTable
                title="Regimen A"
                values={this.state.left}
                onChange={(key, val) => this.handleSideChange("left", key, val)}
                includeBupeInTotal={this.state.includeBupeInTotal}
                extraBelow={
                  <div className="mt-3 rounded-xl border bg-white/90 p-3">
                    <label className="text-sm text-gray-700">Decrease by (%)</label>
                    <div className="mt-1 flex items-center gap-2">
                      <input
                        type="number"
                        step="any"
                        className="w-28 rounded-xl border px-3 py-1 focus:outline-none focus:ring-2 focus:ring-sky-500"
                        value={this.state.leftReducePct}
                        onChange={(e) => this.setState({ leftReducePct: e.target.value })}
                      />
                      <div className="ml-auto text-sm text-gray-600">Adjusted oMEDD</div>
                      <div className="text-lg font-semibold tabular-nums">{mm2(leftReduced)}</div>
                    </div>
                  </div>
                }
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-200">Right regimen</span>
                <button
                  onClick={() => this.clearSide("right")}
                  className="text-xs text-white rounded-lg border px-2 py-1 hover:bg-slate-100 hover:text-slate-900"
                >
                  Clear
                </button>
              </div>

              <OMEDDTable
                title="Regimen B"
                values={this.state.right}
                onChange={(key, val) => this.handleSideChange("right", key, val)}
                includeBupeInTotal={this.state.includeBupeInTotal}
              />
            </div>
          </div>

          <section className="mt-6 md:mt-8">
            <div className="bg-white rounded-2xl shadow p-4 md:p-6">
              <h3 className="text-lg font-semibold mb-2">Totals</h3>
              <div className="text-sm grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="rounded-xl border p-3">
                  <div className="text-gray-600">Regimen A total oMEDD</div>
                  <div className="text-2xl font-bold tabular-nums">{mm2(leftTotal)}</div>
                </div>
                <div className="rounded-xl border p-3">
                  <div className="text-gray-600">Regimen B total oMEDD</div>
                  <div className="text-2xl font-bold tabular-nums">{mm2(rightTotal)}</div>
                </div>
              </div>
            </div>
          </section>

          <footer className="text-xs text-gray-100 mt-6 md:mt-8">
            Factors used: Morphine (oral=1, IV/SC=3), Oxycodone (oral=1.5), Hydromorphone (oral=4, IV/SC=20),
            Codeine (oral=0.15), Tramadol (oral=0.1), Tapentadol (oral=0.4), Fentanyl TD (mcg/hr×2.4).
            Buprenorphine patch shown as range; buprenorphine SL uses 1:40 approximation; verify per local guideline.
          </footer>
        </div>
      </div>
    );
  }
}
